import { DownloadOutlined } from '@ant-design/icons';
import { Button, Card, Col, Descriptions, Row, Skeleton, Space, Statistic, Table, Tag, message } from 'antd';
import { downloadFile, errorMessage } from '../../api/client';
import { useDealerLedger } from '../../api/hooks';
import type { DealerLedgerEntry } from '../../types';
import { date, money } from '../../utils/format';

const TYPE_COLOR: Record<string, string> = {
  Placement: 'blue',
  PrincipalReturn: 'orange',
  ProfitReceipt: 'green',
};

const TYPE_LABEL: Record<string, string> = {
  Placement: 'Investment',
  PrincipalReturn: 'Withdrawal',
  ProfitReceipt: 'ProfitPayment',
};

/** Mirrors the investor Ledger tab exactly — same cards, same descriptions block, same table shape. */
export default function DealerLedgerTab({ dealerId }: { dealerId: number }) {
  const { data, isLoading } = useDealerLedger(dealerId);

  if (isLoading || !data) return <Skeleton active />;

  return (
    <>
      <Space style={{ marginBottom: 16 }} wrap>
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={() =>
            downloadFile(`/reports/dealer-ledger/${dealerId}`, `ledger_statement_${dealerId}.pdf`).catch((err) =>
              message.error(errorMessage(err)),
            )
          }
        >
          Download ledger statement (PDF)
        </Button>
      </Space>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={8}>
          <Card>
            <Statistic title="Current principal" value={money(data.currentPrincipal)} prefix="PKR" />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card>
            <Statistic title="Outstanding profit" value={money(data.outstandingProfit)} prefix="PKR" />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card>
            <Statistic
              title="Total due to me"
              value={money(data.totalDue)}
              prefix="PKR"
              valueStyle={{ color: '#1677ff' }}
            />
          </Card>
        </Col>
      </Row>

      <Descriptions bordered size="small" column={{ xs: 1, md: 2 }} style={{ marginTop: 16 }}>
        <Descriptions.Item label="First investment">
          {data.firstPlacementDate ? date(data.firstPlacementDate) : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="As of">{date(data.asOf)}</Descriptions.Item>
        <Descriptions.Item label="Total invested">{money(data.totalGiven)}</Descriptions.Item>
        <Descriptions.Item label="Total withdrawn">{money(data.totalReturned)}</Descriptions.Item>
        <Descriptions.Item label="Total accrued profit">{money(data.totalAccruedProfit)}</Descriptions.Item>
        <Descriptions.Item label="Total paid profit">{money(data.totalReceivedProfit)}</Descriptions.Item>
      </Descriptions>

      <Card title="Transactions" size="small" style={{ marginTop: 16 }}>
        <Table<DealerLedgerEntry>
          rowKey={(row, index) => `${row.date}-${row.type}-${index}`}
          size="small"
          dataSource={data.entries}
          scroll={{ x: 'max-content' }}
          pagination={{ pageSize: 25, showSizeChanger: true }}
          columns={[
            { title: 'Date', dataIndex: 'date', render: date },
            {
              title: 'Type',
              dataIndex: 'type',
              render: (value: string) => <Tag color={TYPE_COLOR[value] ?? 'default'}>{TYPE_LABEL[value] ?? value}</Tag>,
            },
            { title: 'Description', dataIndex: 'description' },
            { title: 'Debit', dataIndex: 'given', align: 'right', render: (v: number) => (v ? money(v) : '—') },
            { title: 'Credit', dataIndex: 'received', align: 'right', render: (v: number) => (v ? money(v) : '—') },
            { title: 'Principal', dataIndex: 'runningPrincipal', align: 'right', render: money },
          ]}
        />
      </Card>
    </>
  );
}
